import React, { useState } from 'react';
import Heading from '../typography';
import Button from '../button';
import Spinners from '../spinner';
import { ActionButton } from '../vertical-dot/popper';
import { ImportBlock } from '../../constants/style/common.styled';
import { PlainTextWrapper } from '../../pages/settings/system-roles/roles.styled';
import { InputField } from '../../pages/participants/participant.styled';
import { isEmpty } from 'lodash';
import { rem } from '../../utils/style';
import {
  TemplateItem,
  TemplateButton,
  TemplateOption,
} from '../../hooks/custom-hooks/useAllInOneImportExport';

export interface ImportExportBlockProps {
  id?: string;
  title?: string;
  description?: string;
  templateDatas: TemplateItem[];
}

const toCamelCase = (value: string): string => {
  const cleaned = value
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  const [first, ...rest] = cleaned.split(' ');
  return (
    first.charAt(0).toLowerCase() +
    first.slice(1) +
    rest.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')
  );
};

const hasButtons = (item: TemplateItem): item is TemplateItem & { buttons: TemplateButton[] } =>
  (item as any).buttons !== undefined;

const FileInput: React.FC<{
  id: string;
  label: string;
  accept?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
}> = ({ id, label, accept = '.xlsx, .xls', onChange, loading }) => {
  return (
    <div className={`input-file-container import-container ${toCamelCase(id)}Container`}>
      <input className='input-file w-0' id={id} type='file' accept={accept} onChange={onChange} />
      <label
        className='cursor-pointer text-center dt-btn dt-btn-outline-primary dt-btn-sm input-file-trigger sm:text-left import-label'
        htmlFor={id}>
        {loading ? (
          <span className='flex items-center gap-2'>
            <Spinners className='spinner-container' /> {label}
          </span>
        ) : (
          label
        )}
      </label>
    </div>
  );
};

const ImportExportBlock: React.FC<ImportExportBlockProps> = ({
  id,
  title,
  description,
  templateDatas,
}) => {
  const [isImportLoading, setIsImportLoading] = useState(false);

  const handleImport = async (
    event: React.ChangeEvent<HTMLInputElement>,
    importFn?: (event: any) => void
  ): Promise<void> => {
    if (!isEmpty(event.target.files) && typeof importFn === 'function') {
      try {
        setIsImportLoading(true);
        importFn(event);
      } finally {
        setIsImportLoading(false);
        event.target.value = '';
      }
    }
  };

  const renderItem = (item: TemplateItem, itemIndex: number) => {
    const blockId = `${toCamelCase((item as any).id || item.title)}ImportBlock`;
    if (hasButtons(item)) {
      return (
        <ImportBlock key={blockId} id={blockId}>
          {item.title && (
            <Heading variant={'h4'} title={item.title} style={{ marginBottom: rem(8) }} />
          )}
          <PlainTextWrapper>
            <Heading
              variant={'body-s'}
              title={'Quick & easy to use CSV template to import large data in an instant.'}
            />
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-4' key={item.id || `${itemIndex}`}>
                {item.buttons.map((button, buttonIndex) => {
                  if (button.isDropdown && button.options) {
                    // Handle dropdown buttons
                    const actions = button.options.map((option, optionIndex) => {
                      if (option.import) {
                        return {
                          action: (
                            <FileInput
                              id={`${toCamelCase(
                                option.id || option.title
                              )}-${itemIndex}-${buttonIndex}-${optionIndex}`}
                              label={option.title}
                              accept={option.acceptedFileTypes || '.xlsx, .xls'}
                              onChange={(e) => handleImport(e, option.import)}
                              loading={isImportLoading}
                            />
                          ),
                          onClick: () => { },
                        } as const;
                      }
                      return {
                        action: option.title,
                        onClick: () => option.download && option.download(),
                      } as const;
                    });

                    return (
                      <ActionButton
                        key={`${button.id}-${buttonIndex}`}
                        as={'Button'}
                        buttonTitle={button.title}
                        actions={actions}
                        icon={button.title.includes('Download') ? 'cloud_upload' : 'publish'}
                        variant={
                          button.title.includes('Download') ? 'primary-dark' : 'primary-outline'
                        }
                      />
                    );
                  } else {
                    // Handle regular buttons
                    if (button.import) {
                      return (
                        <FileInput
                          key={`${button.id}-${buttonIndex}`}
                          id={`${toCamelCase(
                            button.id || button.title
                          )}-${itemIndex}-${buttonIndex}`}
                          label={button.title}
                          accept={button.acceptedFileTypes || '.xlsx, .xls'}
                          onChange={(e) => handleImport(e, button.import)}
                          loading={isImportLoading}
                        />
                      );
                    }
                    return (
                      <Button
                        key={`${button.id}-${buttonIndex}`}
                        id={`${toCamelCase(button.id || button.title)}Button`}
                        variant={'secondary-white'}
                        title={button.title}
                        icon={'cloud_upload'}
                        onClick={button.download}
                      />
                    );
                  }
                })}
              </div>
            </div>
          </PlainTextWrapper>
        </ImportBlock>
      );
    }

    return (
      <ImportBlock key={blockId} id={blockId}>
        {item.title && (
          <Heading variant={'h4'} title={item.title} style={{ marginBottom: rem(8) }} />
        )}
        <PlainTextWrapper>
          <Heading
            variant={'body-s'}
            title={'Quick & easy to use CSV template to import large data in an instant.'}
          />
          <div className='flex flex-col gap-2'>
            <InputField key={item.id || `${itemIndex}`}>
              <div className='flex gap-2'>
                {'export' in item && item.export && (
                  <Button
                    id={`${toCamelCase(item.id || item.title)}Export`}
                    variant={'secondary-white'}
                    title={`Export`}
                    icon={'cloud_upload'}
                    onClick={(item as any).export}
                  />
                )}
                {'download' in item && item.download && (
                  <Button
                    id={`${toCamelCase(item.id || item.title)}Download`}
                    variant={'secondary-white'}
                    title={`Download Template`}
                    icon={'cloud_upload'}
                    onClick={(item as any).download}
                  />
                )}
                {'customImport' in item && item.customImport ? (
                  item.customImport
                ) : (
                  'import' in item &&
                  item.import && (
                    <FileInput
                      id={`${toCamelCase(item.id || item.title)}Import`}
                      label={`${(item as any).importLabel || (item.acceptedFileTypes === '.json' ? 'Import JSON' : 'Import Bulk XLS')}`}
                      accept={item.acceptedFileTypes || '.xlsx, .xls'}
                      onChange={(e) => handleImport(e, (item as any).import)}
                      loading={isImportLoading}
                    />
                  )
                )}
              </div>
            </InputField>
          </div>
        </PlainTextWrapper>
      </ImportBlock>
    );
  };

  return (
    <div className='flex flex-col gap-2'>
      {templateDatas?.map((item, idx) => renderItem(item, idx))}
    </div>
  );
};

export default ImportExportBlock;
